export type ProjectPermission = 'owner' | 'editor' | 'contributor' | 'viewer';

export interface ProjectMember {
  id: string;
  project_id: string;
  user_id: string | null; // Null for pending invites
  permission: ProjectPermission;
  invited_by: string | null;
  joined_at: string;
  created_at: string;
  updated_at: string;
  // Pending invite fields
  invited_email?: string | null; // Email for pending invites
  invite_sent_at?: string | null; // When invite was sent
  invite_accepted_at?: string | null; // When invite was accepted
  joined_via_share_link?: boolean; // Joined by opening the project's shareable link
  // Joined data from profiles table
  profile?: {
    id: string;
    name: string | null;
    email?: string | null;
  };
}

// Type guard to check if a member is a pending invite
export const isPendingInvite = (member: ProjectMember): boolean => {
  return member.user_id === null && member.invited_email !== null;
};

export interface ProjectWithMembers {
  id: string;
  user_id: string;
  name: string;
  description?: string;
  created_at: string;
  updated_at: string;
  members: ProjectMember[];
  user_permission?: ProjectPermission; // Current user's permission for this project
}

// Permission hierarchy - higher numbers have more permissions
export const PERMISSION_LEVELS: Record<ProjectPermission, number> = {
  viewer: 1,
  contributor: 2,
  editor: 3,
  owner: 4,
};

// Helper functions for permission checking
export const hasPermission = (
  userPermission: ProjectPermission | undefined,
  requiredPermission: ProjectPermission
): boolean => {
  if (!userPermission) return false;
  return PERMISSION_LEVELS[userPermission] >= PERMISSION_LEVELS[requiredPermission];
};

export const canView = (userPermission: ProjectPermission | undefined): boolean => {
  return hasPermission(userPermission, 'viewer');
};

export const canAdd = (userPermission: ProjectPermission | undefined): boolean => {
  return hasPermission(userPermission, 'contributor');
};

export const canEdit = (userPermission: ProjectPermission | undefined): boolean => {
  return hasPermission(userPermission, 'editor');
};

export const canDelete = (userPermission: ProjectPermission | undefined): boolean => {
  return hasPermission(userPermission, 'owner');
};

export const canManageMembers = (userPermission: ProjectPermission | undefined): boolean => {
  return hasPermission(userPermission, 'owner');
};

// Simple permission display names (for badges/tags)
export const PERMISSION_LABELS_SIMPLE: Record<ProjectPermission, string> = {
  owner: 'Owner',
  editor: 'Editor',
  contributor: 'Contributor',
  viewer: 'Viewer',
};

// Detailed permission display names (for dropdowns and tooltips)
export const PERMISSION_LABELS: Record<ProjectPermission, string> = {
  owner: 'Owner (Delete, Add, Edit, Watch)',
  editor: 'Editor (Add, Edit, Watch)',
  contributor: 'Contributor (Add, Watch)',
  viewer: 'Viewer (Watch only)',
};

// Permission descriptions
export const PERMISSION_DESCRIPTIONS: Record<ProjectPermission, string> = {
  owner: 'Full control including deleting the project and managing members',
  editor: 'Can add, edit, and watch content but cannot delete the project',
  contributor: 'Can add content and watch but cannot edit existing content',
  viewer: 'Can only watch content, cannot make any changes',
};
